import React, { useState, useEffect, useRef } from 'react';

// Validation libraries
import { useFormik } from 'formik';
import * as yup from 'yup';

// Hooks
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import useQueryParams from '~/hooks/useQueryParams';

// Action
import { createUser, onnboardingUser } from '~/redux/actions/auth-actions';
import { failure } from '~/redux/actions/snackbar-actions';

// Utils
import { PASSWORD_REGEX, EMAIL_REGEX, PHONE_NO_REGEX } from '~/utils/constants';
import { validateForm, validateFormAsync, realTimeValidation } from '~/utils/formik-validation';
import { MESSAGES } from '~/utils/message';

// components
import ReCAPTCHAComponent from '~/components/General/ReCAPTCHA';
import AccountCompletion from './AccountCompletion';
import OlcButton from '~/components/General/OlcButton';

// MUI Components
import {
  Box,
  Input,
  InputLabel,
  Typography,
  CircularProgress,
  InputAdornment,
} from '@mui/material';
import Visibility from '@mui/icons-material/VisibilityOutlined';
import VisibilityOff from '@mui/icons-material/VisibilityOffOutlined';
import IconButton from '@mui/material/IconButton';

// Icons
import { OnBoardCIcon } from '~/assets/images';

// Styles
import './styles.scss';

const CreateAccountNew = () => {
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [recaptchaError, setRecaptchaError] = useState('');
  const [recaptchaToken, setRecaptchaToken] = useState(null);
  const [onboardingToken, setOnboardingToken] = useState(null);
  const [searchParams] = useSearchParams();
  const [code, setCode] = useState(null);
  const [step, setStep] = useState(1);
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const { allSearchParams } = useQueryParams();

  const childRef = useRef();

  const validationSchema = yup.object().shape({
    firstName: yup.string().required(MESSAGES.AUTH.FIRST_NAME_REQUIRED),
    lastName: yup.string().required(MESSAGES.AUTH.LAST_NAME_REQUIRED),
    email: yup
      .string()
      .matches(EMAIL_REGEX, MESSAGES.AUTH.INVALID_EMAIL)
      .required(MESSAGES.AUTH.EMAIL_REQUIRED),
    password: yup
      .string()
      .matches(PASSWORD_REGEX, MESSAGES.AUTH.INVALID_PASSWORD)
      .required(MESSAGES.AUTH.INVALID_PASSWORD),
  });

  const formik = useFormik({
    initialValues: {
      firstName: '',
      lastName: '',
      email: searchParams.get('email') || '',
      password: '',
    },
    validationSchema: validationSchema,
  });

  useEffect(() => {
    if (searchParams.size > 0) {
      if (searchParams.get('token') && searchParams.get('invited')) {
        let paramsToken = searchParams.get('token');
        let invitedEmail = searchParams.get('invited');
        formik.setFieldValue(
          'email',
          decodeURIComponent((invitedEmail + '').replace(/\%20/g, '+'))
        );
        setCode(paramsToken);
      }
    }
  }, []);

  // For Password
  const handleClickShowPassword = () => {
    setShowPassword(!showPassword);
  };

  const handleMouseDownPassword = (event) => {
    event.preventDefault();
  };

  // reCAPTCHA handlers
  const handleRecaptchaVerify = (token) => {
    setRecaptchaToken(token);
    setRecaptchaError('');
  };

  const handleRecaptchaExpire = () => {
    setRecaptchaToken(null);
    setRecaptchaError('Please complete the reCAPTCHA verification');
  };

  const onBoarding = async (event) => {
    try {
      event.preventDefault();
      const isValid = await validateFormAsync(formik);
      if (!isValid) {
        return;
      }
      if (!recaptchaToken) {
        setRecaptchaError('Please complete the reCAPTCHA verification');
        return;
      }
      const payload = {
        firstName: formik.values.firstName,
        lastName: formik.values.lastName,
        email: formik.values.email,
        password: formik.values.password.trim(),
        secretKey: code,
        recaptcha_token: recaptchaToken,
      };
      setLoading(true);
      const joinTeam = code;
      if (joinTeam) {
        setStep(2);
        return;
      }
      const request = await dispatch(onnboardingUser(payload, joinTeam));
      setLoading(false);
      if (request.message === 'success') {
        setOnboardingToken(request.data?.data?.onboardingToken || null);
        setStep(2);
      } else {
        dispatch(
          failure(
            request?.response?.data?.data?.errors[0]?.msg ||
              request?.response?.data?.message ||
              MESSAGES.GENERAL_ERROR
          )
        );
        if (request?.response?.data?.data?.errors?.length) {
          let serverSideError = request?.response?.data?.data?.errors;
          serverSideError.forEach((error) => {
            formik.errors[error.path] = error.msg;
            formik.touched[error.path] = true;
          });
        }
      }
    } catch (error) {
      setLoading(false);
    } finally {
      childRef.current.reset();
    }
  };

  useEffect(() => {
    formik.touched.firstName = false;
    formik.touched.lastName = false;
    formik.touched.email = false;
    formik.touched.password = false;
  }, []);

  return step === 1 ? (
    <Box className="newCreateAccountWrapper">
      <Box className="topIconWrapper">
        <img src={OnBoardCIcon} alt="OpenLetter" />
      </Box>
      <Box className="createAccountFormWrapper">
        <Box className="createAccountFormHeader">
          <Typography variant="h4">{MESSAGES.SIGN_UP.NEW_TEXT.GET_STARTED_TEXT}</Typography>
          <Typography>{MESSAGES.SIGN_UP.NEW_TEXT.COMPLETE_FORM_TEXT}</Typography>
        </Box>
        <form onSubmit={onBoarding}>
          {/* first name & last name */}
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '12px' }}>
            <Box className="newInputWrapper">
              <InputLabel className="formLabel">
                {MESSAGES.SIGN_UP.INPUT_LABEL_FIRST_NAME}
                <span style={{ position: 'absolute', top: '-3px' }}>*</span>
              </InputLabel>
              <Input
                type="text"
                placeholder={MESSAGES.SIGN_UP.NEW_TEXT.INPUT_PLACEHOLDER_FIRST_NAME}
                name="firstName"
                onChange={formik.handleChange}
                className={`newFormInput ${formik.errors.firstName && formik.touched.firstName ? 'invalid' : ''}`}
                onBlur={realTimeValidation ? formik.handleBlur : undefined}
                value={formik.values.firstName}
              />
              {formik.touched.firstName && formik.errors.firstName && (
                <Typography className="errorMessageNew">
                  <sup>*</sup>
                  {formik.errors.firstName}
                </Typography>
              )}
            </Box>
            <Box className="newInputWrapper">
              <InputLabel className="formLabel">
                {MESSAGES.SIGN_UP.NEW_TEXT.INPUT_LABEL_LAST_NAME}
                <span style={{ position: 'absolute', top: '-3px' }}>*</span>
              </InputLabel>
              <Input
                type="text"
                placeholder={MESSAGES.SIGN_UP.NEW_TEXT.INPUT_PLACEHOLDER_LAST_NAME}
                name="lastName"
                onChange={formik.handleChange}
                className={`newFormInput ${formik.errors.lastName && formik.touched.lastName ? 'invalid' : ''}`}
                onBlur={realTimeValidation ? formik.handleBlur : undefined}
                value={formik.values.lastName}
              />
              {formik.touched.lastName && formik.errors.lastName && (
                <Typography className="errorMessageNew">
                  <sup>*</sup>
                  {formik.errors.lastName}
                </Typography>
              )}
            </Box>
          </Box>
          {/* first name & last name */}

          {/* email & password */}
          <Box className="newInputWrapper">
            <InputLabel className="formLabel">
              {MESSAGES.SIGN_UP.NEW_TEXT.INPUT_LABEL_EMAIL_NEW}
              <span style={{ position: 'absolute', top: '-3px' }}>*</span>
            </InputLabel>
            <Input
              type="text"
              placeholder={MESSAGES.SIGN_UP.NEW_TEXT.EMAIL_PLACEHOLDER_NEW}
              autoComplete="off"
              name="email"
              disabled={code}
              onChange={formik.handleChange}
              className={`newFormInput ${formik.errors.email && formik.touched.email ? 'invalid' : ''}`}
              onBlur={realTimeValidation ? formik.handleBlur : undefined}
              value={formik.values.email}
            />
            {formik.touched.email && formik.errors.email && (
              <Typography className="errorMessageNew">
                <sup>*</sup>
                {formik.errors.email}
              </Typography>
            )}
          </Box>
          <Box className="newInputWrapper">
            <InputLabel className="formLabel">
              {MESSAGES.SIGN_UP.NEW_TEXT.INPUT_LABEL_PASSWORD_NEW}
              <span style={{ position: 'absolute', top: '-3px' }}>*</span>
            </InputLabel>
            <Input
              type={showPassword ? 'text' : 'password'}
              placeholder={MESSAGES.SIGN_UP.NEW_TEXT.INPUT_LABEL_PASSWORD_REQUIRED}
              name="password"
              onChange={formik.handleChange}
              className={`newFormInput ${formik.errors.password && formik.touched.password ? 'invalid' : ''}`}
              onBlur={realTimeValidation ? formik.handleBlur : undefined}
              value={formik.values.password.trim()}
              endAdornment={
                <InputAdornment position="end">
                  <IconButton
                    aria-label="toggle password visibility"
                    onClick={handleClickShowPassword}
                    onMouseDown={handleMouseDownPassword}
                    edge="end"
                    className="passwordIconBtn"
                    tabIndex={-1}
                  >
                    {showPassword ? <Visibility /> : <VisibilityOff />}
                  </IconButton>
                </InputAdornment>
              }
            />
            {formik.touched.password && formik.errors.password && (
              <Typography className="errorMessageNew">
                <sup>*</sup>
                {formik.errors.password}
              </Typography>
            )}
          </Box>

          {/* reCAPTCHA */}
          <Box className="newInputWrapper" style={{ marginTop: '15px' }}>
            <ReCAPTCHAComponent
              onVerify={handleRecaptchaVerify}
              onExpire={handleRecaptchaExpire}
              error={recaptchaError}
              className="signup-recaptcha"
              ref={childRef}
            />
          </Box>

          <OlcButton
            variant="primary"
            type="submit"
            className="submitButton"
          >
            {loading ? (
              <CircularProgress
                sx={{
                  color: 'white',
                  width: '25px !important',
                  height: '25px !important',
                }}
              />
            ) : code ? (
              MESSAGES.SIGN_UP.JOIN_BUTTON
            ) : (
              MESSAGES.SIGN_UP.NEW_TEXT.SUBMIT_BUTTON_NEW
            )}
          </OlcButton>

          {/* <Box className="signInButtonContainer">
            <Divider sx={{ width: '100%' }} />
            <Typography>or sign up with</Typography>
            <Divider sx={{ width: '100%' }} />
          </Box>

          <Button className="googleButton" startIcon={<img src={GoogleCIcon} alt="Google" />}>
            Google
          </Button> */}
        </form>
        <OlcButton
          variant="secondary"
          className="signInButton"
          onClick={() => {
            navigate(`/login${allSearchParams}`);
          }}
        >
          <Typography className="account">
            {MESSAGES.SIGN_UP.SIGN_IN_TEXT}
            <span className="signin-btn">
              <>{MESSAGES.SIGN_UP.NEW_TEXT.LOGIN_TEXT}</>
            </span>
          </Typography>
        </OlcButton>
      </Box>
    </Box>
  ) : (
    <AccountCompletion onBoardedUser={formik.values} onboardingToken={onboardingToken} />
  );
};

export default CreateAccountNew;
